
import React, { useRef, useEffect } from "react";
import {
  PlusIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  ClipboardIcon,
  ScissorsIcon,
  PencilIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  XMarkIcon,
  Squares2X2Icon
} from "@heroicons/react/24/outline";

const ContextMenu = ({
  contextMenu,
  selectedRange,
  clipboardData,
  statementId,
  rows,
  columns,
  getColumnIndex,
  getColumnLetter,
  onClose,
  handlers,
  showNotification,
  contextMenuRef
}) => {
  const MenuBtn = ({ icon: Icon, label, onClick, danger, disabled = false }) => (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`flex items-center w-full px-3 py-2 text-left hover:bg-gray-100 transition-colors ${
        danger ? "text-red-600 hover:text-red-700" : 
        disabled ? "text-gray-400 cursor-not-allowed" : 
        "text-gray-700 hover:text-gray-900"
      }`}
    >
      <Icon className="h-4 w-4 mr-3 flex-shrink-0" />
      <span className="text-sm">{label}</span>
    </button>
  );

  if (!contextMenu.visible) return null;

  // Calculate position to ensure menu stays within viewport
  const calculateMenuPosition = () => {
    const menuWidth = 200;
    const menuHeight = 400; // Max height
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    let x = contextMenu.x;
    let y = contextMenu.y;
    
    // Adjust horizontal position if menu would go off screen
    if (x + menuWidth > viewportWidth) {
      x = viewportWidth - menuWidth - 10;
    }
    
    if (x < 10) {
      x = 10;
    }
    
    // Adjust vertical position if menu would go off screen
    if (y + menuHeight > viewportHeight) {
      y = viewportHeight - menuHeight - 10;
    }
    
    if (y < 10) {
      y = 10;
    }
    
    return { x, y };
  };

  const position = calculateMenuPosition();

  // Determine if we're in a selection range
  const isInSelectionRange = selectedRange && 
    contextMenu.rowIndex >= selectedRange.startRow && 
    contextMenu.rowIndex <= selectedRange.endRow &&
    getColumnIndex(contextMenu.columnId) >= selectedRange.startCol &&
    getColumnIndex(contextMenu.columnId) <= selectedRange.endCol;

  // Determine which clipboard options to show
  const showRangeOptions = isInSelectionRange && selectedRange && 
    (selectedRange.startRow !== selectedRange.endRow || 
     selectedRange.startCol !== selectedRange.endCol);

  return (
    <div
      ref={contextMenuRef}
      className="fixed z-50 bg-white border border-gray-300 shadow-lg rounded-md text-sm overflow-hidden"
      style={{
        top: `${position.y}px`,
        left: `${position.x}px`,
        maxHeight: "400px",
        width: "200px",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)"
      }}
    >
      <div className="overflow-y-auto max-h-[400px]">
        <div className="py-1">
          {/* Edit Cell - Always available */}
          <MenuBtn 
            icon={PencilIcon} 
            label="Edit Cell" 
            onClick={() => {
              handlers.startEditingCell(contextMenu.rowIndex, contextMenu.columnId);
              onClose();
            }} 
          />
          
          <div className="border-t border-gray-200 my-1"></div>
          
          {/* Cut/Copy/Paste - Show either single cell or selection options */}
          {showRangeOptions ? (
            // Range selection options
            <>
              <MenuBtn 
                icon={DocumentDuplicateIcon} 
                label="Copy Selection" 
                onClick={() => {
                  handlers.handleCopySelection();
                  onClose();
                }} 
              />
              <MenuBtn 
                icon={ScissorsIcon} 
                label="Cut Selection" 
                onClick={() => {
                  handlers.handleCutSelection();
                  onClose();
                }} 
                danger 
              />
              <MenuBtn 
                icon={ClipboardIcon} 
                label="Paste to Selection" 
                onClick={() => {
                  const colIndex = getColumnIndex(contextMenu.columnId);
                  handlers.handlePasteToSelection(contextMenu.rowIndex, colIndex);
                  onClose();
                }}
                disabled={!clipboardData?.value && !clipboardData?.rangeData}
              />
            </>
          ) : (
            // Single cell options
            <>
              <MenuBtn 
                icon={ScissorsIcon} 
                label="Cut" 
                onClick={() => {
                  handlers.handleCutCell(contextMenu.rowIndex, contextMenu.columnId);
                  onClose();
                }} 
                danger 
              />
              <MenuBtn 
                icon={DocumentDuplicateIcon} 
                label="Copy" 
                onClick={() => {
                  handlers.handleCopyCell(contextMenu.rowIndex, contextMenu.columnId);
                  onClose();
                }} 
              />
              <MenuBtn 
                icon={ClipboardIcon} 
                label="Paste" 
                onClick={() => {
                  handlers.handlePasteCell(contextMenu.rowIndex, contextMenu.columnId);
                  onClose();
                }}
                disabled={!clipboardData?.value}
              />
            </>
          )}
          
          <div className="border-t border-gray-200 my-1"></div>
          
          {/* Row Operations */}
          <MenuBtn 
            icon={ArrowUpIcon} 
            label="Insert Row Above" 
            onClick={() => {
              handlers.insertRowAbove(contextMenu.rowIndex);
              onClose();
            }} 
          />
          <MenuBtn 
            icon={ArrowDownIcon} 
            label="Insert Row Below" 
            onClick={() => {
              handlers.insertRowBelow(contextMenu.rowIndex);
              onClose();
            }} 
          />
          <MenuBtn 
            icon={TrashIcon} 
            label="Delete Row" 
            onClick={() => {
              handlers.deleteRow(contextMenu.rowIndex);
              onClose();
            }} 
            danger 
          />
          
          <div className="border-t border-gray-200 my-1"></div>
          
          {/* Column Operations */}
          <MenuBtn 
            icon={ArrowLeftIcon} 
            label="Insert Column Left" 
            onClick={() => {
              handlers.insertColumn("left", contextMenu.columnId);
              onClose();
            }} 
          />
          <MenuBtn 
            icon={ArrowRightIcon} 
            label="Insert Column Right" 
            onClick={() => {
              handlers.insertColumn("right", contextMenu.columnId);
              onClose();
            }} 
          />
          <MenuBtn 
            icon={XMarkIcon} 
            label="Delete Column" 
            onClick={() => {
              handlers.deleteColumn(contextMenu.columnId);
              onClose();
            }} 
            danger 
          />
          
          <div className="border-t border-gray-200 my-1"></div>
          
          {/* Selection Clear */}
          <MenuBtn 
            icon={Squares2X2Icon} 
            label={selectedRange ? "Clear Selection" : "Select All"} 
            onClick={() => {
              if (selectedRange) {
                handlers.clearSelection();
              } else {
                // TODO: Implement Select All functionality
                showNotification("Select All feature coming soon", "info");
              }
              onClose();
            }} 
          />
        </div>
      </div>
      
      {/* Scroll indicator when content overflows */}
      <style jsx>{`
        .overflow-y-auto::-webkit-scrollbar {
          width: 6px;
        }
        .overflow-y-auto::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 3px;
        }
        .overflow-y-auto::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 3px;
        }
        .overflow-y-auto::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }
      `}</style>
    </div>
  );
};

export default ContextMenu;
